// Seeded Perlin (improved 2D noise) and Worley (F1/F2 cellular) noise.
// Implementations are self-contained, tiny, and deterministic per seed.

import { mulberry32 } from './rng';

// ---- Perlin ---------------------------------------------------------------
// Standard 2D improved Perlin with seeded permutation table.

function buildPerm(seed: number): Uint8Array {
  const rng = mulberry32(seed);
  const p = new Uint8Array(512);
  const base = new Uint8Array(256);
  for (let i = 0; i < 256; i++) base[i] = i;
  // Fisher-Yates shuffle
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = base[i] as number;
    base[i] = base[j] as number;
    base[j] = tmp;
  }
  for (let i = 0; i < 512; i++) p[i] = base[i & 255] as number;
  return p;
}

function fade(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function grad2(hash: number, x: number, y: number): number {
  const h = hash & 7;
  const u = h < 4 ? x : y;
  const v = h < 4 ? y : x;
  return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
}

export function perlin2(p: Uint8Array, x: number, y: number): number {
  const X = Math.floor(x) & 255;
  const Y = Math.floor(y) & 255;
  const xf = x - Math.floor(x);
  const yf = y - Math.floor(y);
  const u = fade(xf);
  const v = fade(yf);
  const A = (p[X] as number) + Y;
  const B = (p[X + 1] as number) + Y;
  const aa = p[A] as number;
  const ab = p[A + 1] as number;
  const ba = p[B] as number;
  const bb = p[B + 1] as number;
  return lerp(
    lerp(grad2(aa, xf, yf),     grad2(ba, xf - 1, yf), u),
    lerp(grad2(ab, xf, yf - 1), grad2(bb, xf - 1, yf - 1), u),
    v,
  );
}

export function makePerlin(seed: number): (x: number, y: number) => number {
  const p = buildPerm(seed);
  return (x, y) => perlin2(p, x, y);
}

/** Sum of `octaves` Perlin layers. Returns roughly [-1, 1]. */
export function fbm2(seed: number, x: number, y: number, octaves: number, persistence: number): number {
  const noise = makePerlin(seed);
  let amp = 1;
  let freq = 1;
  let sum = 0;
  let max = 0;
  for (let i = 0; i < octaves; i++) {
    sum += amp * noise(x * freq, y * freq);
    max += amp;
    amp *= persistence;
    freq *= 2;
  }
  return max === 0 ? 0 : sum / max;
}

// ---- Worley ---------------------------------------------------------------
// Generates `n` random sites within the [0, w) × [0, h) box, then for each
// query returns the F1 (and optionally F2) Euclidean distance to nearest site.
// For pixel grids it's fastest to grid-bucket the sites and check 9 neighbors.

export type WorleySites = { xs: Float32Array; ys: Float32Array; cellSize: number; cols: number; rows: number; bucket: number[][] };

export function buildWorley(
  seed: number,
  count: number,
  w: number,
  h: number,
): WorleySites {
  const rng = mulberry32(seed);
  const xs = new Float32Array(count);
  const ys = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    xs[i] = rng() * w;
    ys[i] = rng() * h;
  }
  const cellSize = Math.max(1, Math.sqrt((w * h) / Math.max(1, count)));
  const cols = Math.max(1, Math.ceil(w / cellSize));
  const rows = Math.max(1, Math.ceil(h / cellSize));
  const bucket: number[][] = Array.from({ length: cols * rows }, () => []);
  for (let i = 0; i < count; i++) {
    const cx = Math.min(cols - 1, Math.floor((xs[i] as number) / cellSize));
    const cy = Math.min(rows - 1, Math.floor((ys[i] as number) / cellSize));
    (bucket[cy * cols + cx] as number[]).push(i);
  }
  return { xs, ys, cellSize, cols, rows, bucket };
}

/** Returns [F1, F2] Euclidean distances and the nearest site index. */
export function worleyDist(
  s: WorleySites,
  px: number,
  py: number,
): { f1: number; f2: number; idx: number } {
  const cx = Math.min(s.cols - 1, Math.max(0, Math.floor(px / s.cellSize)));
  const cy = Math.min(s.rows - 1, Math.max(0, Math.floor(py / s.cellSize)));
  let f1 = Infinity, f2 = Infinity, idx = -1;
  for (let oy = -1; oy <= 1; oy++) {
    const yy = cy + oy;
    if (yy < 0 || yy >= s.rows) continue;
    for (let ox = -1; ox <= 1; ox++) {
      const xx = cx + ox;
      if (xx < 0 || xx >= s.cols) continue;
      const list = s.bucket[yy * s.cols + xx] as number[];
      for (let k = 0; k < list.length; k++) {
        const i = list[k] as number;
        const dx = px - (s.xs[i] as number);
        const dy = py - (s.ys[i] as number);
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < f1) { f2 = f1; f1 = d; idx = i; }
        else if (d < f2) { f2 = d; }
      }
    }
  }
  return { f1, f2, idx };
}
